import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Clock3,
  GitBranchPlus,
  Layers3,
  TrendingUp,
  UsersRound,
} from "lucide-react";

import axiosClient from "../../api/axiosClient";

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

function formatNumber(value) {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric)) return "0";
  return new Intl.NumberFormat().format(numeric);
}

function getPercent(numerator, denominator) {
  const top = Number(numerator ?? 0);
  const bottom = Number(denominator ?? 0);
  if (!Number.isFinite(top) || !Number.isFinite(bottom) || bottom <= 0) return null;
  return Math.round((top / bottom) * 100);
}

function clampText(value, fallback = "-") {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

function StepMetricCard({ label, value, hint, tone = "slate", icon: Icon }) {
  const tones = {
    slate: "border-slate-200 bg-white text-slate-900",
    blue: "border-sky-200 bg-sky-50/80 text-sky-950",
    emerald: "border-emerald-200 bg-emerald-50/80 text-emerald-950",
    amber: "border-amber-200 bg-amber-50/80 text-amber-950",
  };

  const labelTones = {
    slate: "text-slate-500",
    blue: "text-sky-700",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
  };

  return (
    <div className={`relative overflow-hidden rounded-xl border px-4 py-3 ${tones[tone]}`}>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(15,23,42,0.04),rgba(14,165,233,0.10),rgba(255,255,255,0))]" />
      <div className="pointer-events-none absolute -right-10 top-0 h-24 w-24 rounded-full bg-sky-200/20 blur-2xl" />
      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <p
            className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${
              labelTones[tone] || labelTones.slate
            }`}
          >
            {label}
          </p>
          {Icon ? <Icon className="h-4 w-4 text-slate-400" /> : null}
        </div>
        <div className="mt-2 space-y-1.5">
          <div className="text-sm font-semibold leading-5">{value}</div>
          {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
        </div>
      </div>
    </div>
  );
}

function InsightRow({ label, value, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-white text-slate-800",
    blue: "border-sky-200 bg-sky-50 text-sky-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  };

  return (
    <div className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${tones[tone]}`}>
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  );
}

export default function FlowAnalyticsDashboard() {
  const [summary, setSummary] = useState(null);
  const [topSteps, setTopSteps] = useState([]);
  const [stepJourney, setStepJourney] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryRes, stepsRes, journeyRes] = await Promise.all([
          axiosClient.get("/flow-analytics/summary"),
          axiosClient.get("/flow-analytics/most-triggered-steps"),
          axiosClient.get("/flow-analytics/step-journey-breakdown"),
        ]);

        setSummary(summaryRes?.data ?? null);
        setTopSteps(Array.isArray(stepsRes?.data) ? stepsRes.data : []);
        setStepJourney(Array.isArray(journeyRes?.data) ? journeyRes.data : []);
      } catch (error) {
        toast.error("Failed to load flow analytics data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const topTriggeredStep = useMemo(() => {
    if (topSteps.length > 0) return topSteps[0];
    if (summary?.topStepTriggered) {
      return {
        stepName: summary.topStepTriggered,
        count: summary.topStepCount ?? 0,
      };
    }
    return null;
  }, [summary, topSteps]);

  const topDropOffStep = useMemo(() => {
    if (stepJourney.length === 0) return null;
    return [...stepJourney].sort(
      (left, right) => Number(right?.dropOff ?? 0) - Number(left?.dropOff ?? 0)
    )[0];
  }, [stepJourney]);

  const totalReached = useMemo(
    () => stepJourney.reduce((sum, step) => sum + Number(step?.totalReached ?? 0), 0),
    [stepJourney]
  );

  const totalClickedNext = useMemo(
    () => stepJourney.reduce((sum, step) => sum + Number(step?.clickedNext ?? 0), 0),
    [stepJourney]
  );

  const totalDropOff = useMemo(
    () => stepJourney.reduce((sum, step) => sum + Number(step?.dropOff ?? 0), 0),
    [stepJourney]
  );

  const advanceRate = getPercent(totalClickedNext, totalReached);
  const dropOffRate = getPercent(totalDropOff, totalReached);

  const chartData = useMemo(
    () =>
      topSteps.map((step, index) => ({
        ...step,
        rank: index + 1,
        shortName: clampText(step?.stepName, `Step ${index + 1}`),
      })),
    [topSteps]
  );

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-80px)] bg-gradient-to-b from-white via-slate-50 to-slate-100 px-8 pb-4 pt-6 sm:px-10">
        <div className="mx-auto flex max-w-[1600px] flex-col gap-4">
          <div className="h-16 animate-pulse rounded-xl bg-white/80" />
          <div className="h-72 animate-pulse rounded-xl bg-white/80" />
          <div className="grid gap-4 xl:grid-cols-2">
            <div className="h-80 animate-pulse rounded-xl bg-white/80" />
            <div className="h-80 animate-pulse rounded-xl bg-white/80" />
          </div>
        </div>
      </div>
    );
  }

  if (!summary && topSteps.length === 0 && stepJourney.length === 0) {
    return (
      <div className="min-h-[calc(100vh-80px)] bg-gradient-to-b from-white via-slate-50 to-slate-100 px-8 pb-4 pt-6 sm:px-10">
        <div className="mx-auto flex max-w-[1600px] flex-col gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600">
              <BarChart3 className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-[16px] font-semibold text-slate-900">
                Flow Analytics
              </h1>
              <p className="text-sm text-slate-500">
                Monitor automation flow performance, drop-offs, and step activity from one workspace.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <Activity className="h-5 w-5 text-slate-500" />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-slate-900">
              No flow analytics available yet
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              This page becomes useful once automation flows start recording step activity and journey outcomes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-80px)] bg-gradient-to-b from-white via-slate-50 to-slate-100 px-8 pb-4 pt-6 sm:px-10">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600">
            <BarChart3 className="h-4 w-4 text-white" />
          </div>
          <div>
            <h1 className="text-[16px] font-semibold text-slate-900">
              Flow Analytics
            </h1>
            <p className="text-sm text-slate-500">
              Monitor automation flow performance, spot drop-off steps, and understand how contacts move across flow journeys.
            </p>
          </div>
        </div>

        <div className="rounded-md border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-5">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Automation snapshot
            </div>

            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
              <div className="min-w-0 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-xl font-semibold text-emerald-700">
                    F
                  </div>
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-[16px] font-semibold text-slate-900">
                        Automation health view
                      </h2>
                      <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] text-slate-700">
                        <Clock3 className="h-3.5 w-3.5 text-slate-400" />
                        Last execution {formatDateTime(summary?.lastExecutedAt)}
                      </span>
                    </div>
                    <p className="max-w-2xl text-sm text-slate-500">
                      Use this view to understand which steps attract the most traffic, where contacts drop, and which flow path needs attention first.
                    </p>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <StepMetricCard
                    label="Executions"
                    tone="blue"
                    icon={Activity}
                    value={formatNumber(summary?.totalExecutions)}
                    hint="Total step executions recorded"
                  />
                  <StepMetricCard
                    label="Unique contacts"
                    tone="emerald"
                    icon={UsersRound}
                    value={formatNumber(summary?.uniqueContacts)}
                    hint="Contacts that entered tracked flows"
                  />
                  <StepMetricCard
                    label="Top triggered step"
                    tone="slate"
                    icon={TrendingUp}
                    value={clampText(topTriggeredStep?.stepName || summary?.topStepTriggered, "No step data")}
                    hint={
                      topTriggeredStep
                        ? `Triggered ${formatNumber(topTriggeredStep?.count || summary?.topStepCount)} times`
                        : "No trigger counts available"
                    }
                  />
                  <StepMetricCard
                    label="Biggest drop-off"
                    tone="amber"
                    icon={AlertTriangle}
                    value={clampText(topDropOffStep?.templateName, "No drop-off data")}
                    hint={
                      topDropOffStep
                        ? `${formatNumber(topDropOffStep?.dropOff)} exits from this step`
                        : "No journey exits recorded"
                    }
                  />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Operational view
                    </div>
                    <div className="mt-1 text-[16px] font-semibold text-slate-900">
                      Journey performance
                    </div>
                  </div>
                  <div className="inline-flex items-center justify-center rounded-full border border-sky-100 bg-sky-50 px-3 py-1 text-[11px] font-semibold text-sky-700">
                    {stepJourney.length} tracked steps
                  </div>
                </div>

                <div className="mt-4 space-y-2.5">
                  <InsightRow
                    label="Average advance rate"
                    value={advanceRate === null ? "-" : `${advanceRate}%`}
                    tone="blue"
                  />
                  <InsightRow
                    label="Drop-off load"
                    value={dropOffRate === null ? "-" : `${dropOffRate}%`}
                    tone="amber"
                  />
                  <InsightRow
                    label="Total exits recorded"
                    value={formatNumber(totalDropOff)}
                    tone="slate"
                  />
                  <InsightRow
                    label="Most likely next fix"
                    value={clampText(topDropOffStep?.templateName, "Review journey design")}
                    tone="emerald"
                  />
                </div>

                <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-white/70 px-3 py-2.5">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                    Recommended use
                  </p>
                  <p className="mt-1.5 text-sm leading-6 text-slate-600">
                    Use this dashboard to identify the busiest step, find the largest exit point, and decide which automation path should be reviewed first.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Step traffic
                </div>
                <h2 className="mt-1 text-[16px] font-semibold text-slate-900">
                  Most triggered steps
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Compare which steps receive the highest amount of flow traffic.
                </p>
              </div>
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-600">
                {topSteps.length} ranked steps
              </div>
            </div>

            {topSteps.length === 0 ? (
              <div className="mt-5 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                No step execution data available.
              </div>
            ) : (
              <>
                <div className="mt-5 h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 16, left: -12, bottom: 0 }}>
                      <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="shortName"
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(148,163,184,0.08)" }}
                        contentStyle={{
                          borderRadius: 12,
                          border: "1px solid #e2e8f0",
                          boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
                        }}
                      />
                      <Bar dataKey="count" fill="#0f766e" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-5 overflow-hidden rounded-xl border border-slate-200">
                  <table className="min-w-full text-sm text-slate-700">
                    <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left">Step</th>
                        <th className="px-4 py-3 text-left">Executions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {topSteps.slice(0, 5).map((step, index) => (
                        <tr key={`${step?.stepName || "step"}-${index}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                                {index + 1}
                              </span>
                              <span className="font-medium text-slate-900">
                                {clampText(step?.stepName, `Step ${index + 1}`)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-700">
                            {formatNumber(step?.count)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Journey breakdown
                </div>
                <h2 className="mt-1 text-[16px] font-semibold text-slate-900">
                  Step progression analysis
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Review reached volume, forward movement, and exits at each step.
                </p>
              </div>
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-600">
                <GitBranchPlus className="mr-1.5 h-3.5 w-3.5" />
                {stepJourney.length} journey rows
              </div>
            </div>

            {stepJourney.length === 0 ? (
              <div className="mt-5 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                No journey breakdown data available.
              </div>
            ) : (
              <div className="mt-5 space-y-3">
                {stepJourney.map((step, index) => {
                  const nextRate = getPercent(step?.clickedNext, step?.totalReached);
                  const exitRate = getPercent(step?.dropOff, step?.totalReached);
                  const rowTone =
                    Number(step?.dropOff ?? 0) > 0 ? "border-amber-200 bg-amber-50/60" : "border-slate-200 bg-slate-50/60";

                  return (
                    <div
                      key={`${step?.templateName || "journey"}-${index}`}
                      className={`rounded-xl border px-4 py-3 ${rowTone}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-sm font-semibold text-slate-900">
                              {clampText(step?.templateName, `Step ${index + 1}`)}
                            </h3>
                            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">
                              {formatNumber(step?.totalReached)} reached
                            </span>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1">
                              <ArrowRight className="h-3.5 w-3.5" />
                              {formatNumber(step?.clickedNext)} moved next
                            </span>
                            <span>|</span>
                            <span>{formatNumber(step?.dropOff)} exits</span>
                            <span>|</span>
                            <span>
                              Next step {clampText(step?.nextStepName || step?.nextStepId, "-")}
                            </span>
                          </div>
                        </div>

                        <div className="grid min-w-[180px] gap-2 sm:min-w-[220px]">
                          <InsightRow
                            label="Advance rate"
                            value={nextRate === null ? "-" : `${nextRate}%`}
                            tone="blue"
                          />
                          <InsightRow
                            label="Exit rate"
                            value={exitRate === null ? "-" : `${exitRate}%`}
                            tone={Number(step?.dropOff ?? 0) > 0 ? "amber" : "slate"}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                What to do with this
              </div>
              <h2 className="mt-1 text-[16px] font-semibold text-slate-900">
                Operator guidance
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                This page is useful only if it tells the team what to inspect next.
              </p>
            </div>
            <div className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-medium text-emerald-700">
              <Layers3 className="mr-1.5 h-3.5 w-3.5" />
              Action-oriented analytics
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <StepMetricCard
              label="Look first"
              tone="amber"
              icon={AlertTriangle}
              value={clampText(topDropOffStep?.templateName, "Largest drop-off step")}
              hint="Review this node first because it is losing the most contacts."
            />
            <StepMetricCard
              label="Traffic anchor"
              tone="blue"
              icon={TrendingUp}
              value={clampText(topTriggeredStep?.stepName, "Top traffic step")}
              hint="This is the busiest point in your current automation traffic."
            />
            <StepMetricCard
              label="Flow health"
              tone="emerald"
              icon={Activity}
              value={advanceRate === null ? "-" : `${advanceRate}% average advance rate`}
              hint="Use this to judge whether contacts keep moving through the journey."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
